import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetCurrentSessionQuery } from "../store/api/guestApi";
import { sessionVerified } from "../store/slices/authSlice";

/**
 * Confirms with the server that a session restored from localStorage is still live.
 *
 * The app used to treat a stored session blob as authoritative: if it carried a
 * future `sessionExpiry`, the guest got full access. Nothing checked with the
 * backend, so a session ended server-side — front desk checkout, a staff action,
 * a room turnover — kept working in the UI until some unrelated request happened
 * to 401.
 *
 * The restore stays optimistic so the guest isn't held behind a spinner. This
 * runs alongside it and tears the session down if the server disagrees. A 401
 * from the check is handled centrally in apiSlice's baseQueryWithReauth, which
 * dispatches sessionExpired and resets the RTK Query cache.
 */
export const useSessionGuard = () => {
  const dispatch = useDispatch();
  const { sessionId, hasActiveSession, isSessionVerified } = useSelector(
    (state) => state.auth,
  );
  const [verifySession] = useLazyGetCurrentSessionQuery();

  // Only verify once per session id, so this doesn't re-fire on every render.
  const verifiedForRef = useRef(null);

  useEffect(() => {
    if (!sessionId || !hasActiveSession || isSessionVerified) return;
    if (verifiedForRef.current === sessionId) return;

    verifiedForRef.current = sessionId;

    let cancelled = false;

    (async () => {
      try {
        const result = await verifySession().unwrap();
        if (cancelled) return;
        dispatch(
          sessionVerified({
            expiresAt: result?.data?.expiresAt,
            status: result?.data?.status,
          }),
        );
      } catch {
        // A 401/403 has already been turned into sessionExpired by the base
        // query. Anything else (offline, server hiccup) must not log the guest
        // out — allow a retry on the next mount.
        if (!cancelled) verifiedForRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, hasActiveSession, isSessionVerified, verifySession, dispatch]);
};

export default useSessionGuard;
